import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { deliveryAreasApi, type DeliveryArea } from '../crmApi'
import { apiErrorMessage } from '../api'
import { useDialogs } from '../components/Dialogs'

export function DeliveryAreas() {
  const { confirm } = useDialogs()
  const navigate = useNavigate()
  const [areas, setAreas] = useState<DeliveryArea[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [editId, setEditId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [includeDeleted, setIncludeDeleted] = useState(false)

  async function reload() {
    setLoading(true)
    try {
      setAreas(await deliveryAreasApi.list(includeDeleted))
      setError(null)
    } catch (e) {
      setError(describe(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDeleted])

  async function create() {
    if (!newName.trim()) return
    try {
      await deliveryAreasApi.create(newName.trim())
      setNewName('')
      await reload()
    } catch (e) {
      setError(describe(e))
    }
  }

  async function save(id: number) {
    try {
      await deliveryAreasApi.update(id, editName.trim())
      setEditId(null)
      await reload()
    } catch (e) {
      setError(describe(e))
    }
  }

  async function remove(area: DeliveryArea) {
    if (!(await confirm(`Radera leveransområdet "${area.name}"?`, { danger: true, confirmLabel: 'Radera' }))) return
    try {
      await deliveryAreasApi.remove(area.id)
      await reload()
    } catch (e) {
      setError(describe(e))
    }
  }

  async function restore(area: DeliveryArea) {
    try {
      await deliveryAreasApi.restore(area.id)
      await reload()
    } catch (e) {
      setError(describe(e))
    }
  }

  return (
    <div>
      <h1>Leveransområden</h1>
      {error && <p className="error">{error}</p>}

      <div className="card row-form">
        <input placeholder="Nytt leveransområde…" value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void create()} />
        <button className="primary" onClick={() => void create()}>Lägg till</button>
        <label className="inline" style={{ marginLeft: 'auto' }}>
          <input type="checkbox" checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)} /> Visa borttagna
        </label>
      </div>

      {loading ? <p className="muted">Laddar…</p> : (
        <table className="grid">
          <thead><tr><th>Namn</th><th>Antal kunder</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {areas.map((a) => (
              <tr key={a.id} className={a.isDeleted ? 'muted' : undefined}>
                <td>
                  {editId === a.id
                    ? <input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
                    : a.name}
                </td>
                <td>{a.customerCount}</td>
                <td>{a.isDeleted ? <span className="badge muted-badge">Borttagen</span> : <span className="badge">Aktiv</span>}</td>
                <td className="actions">
                  {editId === a.id ? (
                    <>
                      <button className="primary" onClick={() => void save(a.id)}>Spara</button>
                      <button onClick={() => setEditId(null)}>Avbryt</button>
                    </>
                  ) : a.isDeleted ? (
                    <button onClick={() => void restore(a)}>Återställ</button>
                  ) : (
                    <>
                      <button onClick={() => navigate(`/delivery-areas/${a.id}`)}>Hantera</button>
                      <button onClick={() => { setEditId(a.id); setEditName(a.name) }}>Ändra</button>
                      <button onClick={() => void remove(a)}>Radera</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {areas.length === 0 && <tr><td colSpan={4} className="muted">Inga leveransområden{includeDeleted ? '' : ' ännu'}.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  )
}

const describe = apiErrorMessage
